/**
 * @fileoverview Painel de Validações em Tempo Real
 * 
 * @module ValidationPanel
 * @description
 * Exibe validações críticas e warnings das premissas de valuation.
 * Integra com useValuationValidations hook.
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import { IconCheckCircle, IconAlertTriangle } from '../../utils/icons';
import { Lightbulb } from 'lucide-react';

import { ValidationResult, ValidationSeverity } from '../../hooks/useValuationValidations';
import { touchFriendlyIconButton } from '../../utils/mobileFriendlyStyles';

// ============================================================================
// TYPES
// ============================================================================

interface ValidationPanelProps {
  validations: ValidationResult[];
  title?: string;
  collapsible?: boolean;
  showSuccessful?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const getSeverityIcon = (severity: ValidationSeverity) => {
  switch (severity) {
    case 'error':
      return <ErrorIcon />;
    case 'warning':
      return <Warning />;
    case 'success':
      return <CheckCircle />;
    default:
      return <Info />;
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export const ValidationPanel: React.FC<ValidationPanelProps> = ({
  validations,
  title = 'Validações das Premissas',
  collapsible = true,
  showSuccessful = false,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  // Filtrar validações problemáticas
  const problematicValidations = validations.filter(
    v => v.severity === 'error' || v.severity === 'warning'
  );
  const successValidations = validations.filter(v => v.severity === 'success');
  const hasProblems = problematicValidations.length > 0;

  // Exibir validações
  const displayValidations = showSuccessful ? validations : problematicValidations;

  if (displayValidations.length === 0 && !showSuccessful) {
    return (
      <Alert severity="success" icon={<IconCheckCircle size={20} />}>
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconCheckCircle size={18} /> Todas as validações OK
        </AlertTitle>
        <Typography variant="body2">
          {validations.length} validação(ões) passou(aram) com sucesso.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header com contadores */}
      {collapsible && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            cursor: 'pointer',
            p: 1,
            bgcolor: hasProblems ? 'warning.lighter' : 'success.lighter',
            borderRadius: 1,
            mb: 1,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {title}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {problematicValidations.length > 0 && (
                <>
                  <IconAlertTriangle size={14} />
                  {problematicValidations.length} alerta(s)
                </>
              )}
              {problematicValidations.length === 0 && (
                <>
                  <IconCheckCircle size={14} />
                  {successValidations.length} OK
                </>
              )}
            </Typography>
            <IconButton sx={touchFriendlyIconButton}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Validações */}
      <Collapse in={expanded}>
        <Stack spacing={1}>
          {displayValidations.map(validation => (
            <Alert
              key={validation.id}
              severity={validation.severity}
              icon={getSeverityIcon(validation.severity)}
            >
              <AlertTitle>{validation.message}</AlertTitle>
              {validation.details && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {validation.details}
                </Typography>
              )}
              {validation.action && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Lightbulb size={14} />
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {validation.action}
                  </Typography>
                </Stack>
              )}
            </Alert>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

/**
 * Versão compacta para exibição inline
 */
export const ValidationBadge: React.FC<{ validations: ValidationResult[] }> = ({
  validations,
}) => {
  const hasErrors = validations.some(v => v.severity === 'error' && !v.passed);
  const hasWarnings = validations.some(v => v.severity === 'warning' && !v.passed);

  if (!hasErrors && !hasWarnings) {
    return (
      <Box display="inline-flex" alignItems="center" gap={0.5}>
        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
        <Typography variant="caption" color="success.main" fontWeight="bold">
          Validações OK
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="inline-flex" alignItems="center" gap={0.5}>
      {hasErrors && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
      {!hasErrors && hasWarnings && <Warning sx={{ fontSize: 16, color: 'warning.main' }} />}
      <Typography
        variant="caption"
        color={hasErrors ? 'error.main' : 'warning.main'}
        fontWeight="bold"
      >
        {hasErrors ? 'Erros Críticos' : `${validations.filter(v => !v.passed).length} Alerta(s)`}
      </Typography>
    </Box>
  );
};

export default ValidationPanel;
